import { tasksApi } from './client'
import type { Task, TaskPriority, TaskStatus } from './tasks'

export interface PersonalNote {
  id: string
  ownerId: string
  title: string
  content: string
  tags: string[]
  isPinned: boolean
  isArchived: boolean
  convertedTaskId?: string
  convertedTaskNumber?: string
  convertedAt?: string
  visibility: 'private'
  createdAt: string
  updatedAt: string
}

export interface NotePayload {
  title: string
  content: string
  tags?: string[]
  isPinned?: boolean
  isArchived?: boolean
}

export interface NoteFilters {
  search?: string
  status?: 'active' | 'archived' | 'converted'
  pinned?: boolean
  sort?: 'updated_at' | 'created_at' | 'title'
}

export interface ConvertNotePayload {
  boardId: string
  title: string
  description: string
  assigneeId?: string
  assigneeName?: string
  status: TaskStatus
  priority: TaskPriority
  startDate?: string
  dueDate?: string
  estimatedHours?: number
  labels?: string[]
  keepNoteLink: boolean
  archiveNoteAfterConversion: boolean
  allowRepeatConversion?: boolean
}

export interface ConvertNoteResponse {
  noteId: string
  task: Task
  noteArchived: boolean
  convertedAt: string
}

const BASE = '/api/v1/notes'

function query(filters: NoteFilters = {}) {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.status) params.set('status', filters.status)
  if (filters.pinned !== undefined) params.set('pinned', String(filters.pinned))
  if (filters.sort) params.set('sort', filters.sort)
  const q = params.toString()
  return q ? `?${q}` : ''
}

export const notesService = {
  list: (filters: NoteFilters = {}) => tasksApi.get<PersonalNote[]>(`${BASE}${query(filters)}`),
  get: (id: string) => tasksApi.get<PersonalNote>(`${BASE}/${encodeURIComponent(id)}`),
  create: (payload: NotePayload) => tasksApi.post<PersonalNote>(BASE, payload),
  update: (id: string, payload: NotePayload) => tasksApi.put<PersonalNote>(`${BASE}/${encodeURIComponent(id)}`, payload),
  delete: (id: string) => tasksApi.delete<void>(`${BASE}/${encodeURIComponent(id)}`),
  archive: (id: string) => tasksApi.post<PersonalNote>(`${BASE}/${encodeURIComponent(id)}/archive`),
  unarchive: (id: string) => tasksApi.post<PersonalNote>(`${BASE}/${encodeURIComponent(id)}/unarchive`),
  pin: (id: string) => tasksApi.post<PersonalNote>(`${BASE}/${encodeURIComponent(id)}/pin`),
  unpin: (id: string) => tasksApi.post<PersonalNote>(`${BASE}/${encodeURIComponent(id)}/unpin`),
  convertToTask: (id: string, payload: ConvertNotePayload) => tasksApi.post<ConvertNoteResponse>(`${BASE}/${encodeURIComponent(id)}/convert-to-task`, payload),
}
